import type React from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { Section } from '../models';

interface SectionContextValue {
  section: Section | null;
  setSection: (section: Section | null) => void;
  setSectionValues: (keyValuePairs: object) => void;
}

const SectionContext = createContext<SectionContextValue | null>(null);

export function useSectionContext(): SectionContextValue {
  const context = useContext(SectionContext);
  if (!context) {
    throw new Error('useSectionContext must be used within a SectionProvider');
  }
  return context;
}

interface SectionProviderProps {
  children: React.ReactNode;
  initialSection?: Section | null;
}

export function SectionProvider({ children, initialSection = null }: SectionProviderProps) {
  const [section, setSectionState] = useState<Section | null>(initialSection);
  const hasBeenSetInternallyRef = useRef(false);
  const prevInitialSectionRef = useRef(initialSection);

  useEffect(() => {
    const currentNodeId = prevInitialSectionRef.current?.currentNodeId;
    const newNodeId = initialSection?.currentNodeId;
    const initialSectionChanged = currentNodeId !== newNodeId;

    if (initialSectionChanged) {
      prevInitialSectionRef.current = initialSection;
      setSectionState(initialSection);
      hasBeenSetInternallyRef.current = false;
    }
  }, [initialSection]);

  const setSection = useCallback((newSection: Section | null) => {
    hasBeenSetInternallyRef.current = true;

    if (newSection != null) {
      setSectionState(newSection.clone());
    } else {
      setSectionState(null);
    }
  }, []);

  const setSectionValues = useCallback((keyValuePairs: object) => {
    setSectionState((currentSection) => {
      if (!currentSection) return null;

      const updatedSection = currentSection.clone();
      updatedSection.setFieldValues(keyValuePairs);
      return updatedSection;
    });
  }, []);

  const value: SectionContextValue = useMemo(
    () => ({
      section,
      setSection,
      setSectionValues,
    }),
    [section, setSection, setSectionValues],
  );

  return <SectionContext.Provider value={value}>{children}</SectionContext.Provider>;
}
